import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { useState } from "react";
import axiosClient from "../../axiosClient";
import { useNavigate } from "react-router";
import { Assignment } from "../../types";
import { BsClock, BsListOl } from "react-icons/bs";

/** Hardcoded until GET /subjects/:subjectId/exam-configurations exists — see docs/timed-exam-configurations.md */
const EXAM_CONFIGURATION_OPTIONS: {
  id: string;
  description: string;
  totalQuestions: number;
  /** Wall-clock exam length (minutes) — sent as `examDuration` */
  durationMinutes: number;
  /** Server uses this for question pacing; 2h = 7200s ÷ 90 questions */
  secondsPerQuestion: number;
}[] = [
  {
    id: "frcophth-part1-standard",
    description: "Practice exam",
    totalQuestions: 90,
    durationMinutes: 120,
    secondsPerQuestion: 80,
  },
];

function TestExamModal(props: {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  subjectId: number;
  subjectTotalQuestions: number;
}) {
  const handleClose = () => props.setShowModal(false);
  const navigate = useNavigate();
  const [selectedConfigId, setSelectedConfigId] = useState<string>(
    EXAM_CONFIGURATION_OPTIONS[0]?.id ?? "",
  );

  const selectedConfig = EXAM_CONFIGURATION_OPTIONS.find(
    (c) => c.id === selectedConfigId,
  );
  const hasEnoughQuestions =
    props.subjectTotalQuestions >= (selectedConfig?.totalQuestions ?? 0);

  return (
    <>
      <Modal show={props.showModal} onHide={handleClose} centered>
        <Modal.Header closeButton closeLabel="close modal">
          <Modal.Title
            style={{
              fontSize: "1rem",
            }}
          >
            Timed exam
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            Choose an exam format for this course. Questions are drawn from
            across all modules in this subject.
          </p>

          <Form
            id="timed-exam-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedConfig || !hasEnoughQuestions) return;

              const testExam: {
                assignment: Assignment;
                submission: {
                  id: number;
                  submittedDate: Date | null;
                  assignmentId: number;
                  studentId: number;
                  score: number | null;
                };
              } = (
                await axiosClient.post(`/assignments-practice`, {
                  subjectId: props.subjectId,
                  selectedModules: {
                    "module-all": selectedConfig.totalQuestions,
                  },
                  secondsPerQuestion: selectedConfig.secondsPerQuestion,
                })
              ).data;

              navigate("/assignment", {
                state: {
                  submissionId: testExam.submission.id,
                  assignmentId: testExam.assignment.id,
                  assignmentName: testExam.assignment.name,
                },
              });
            }}
          >
            <Form.Group className="mb-0">
              <Form.Label className="small fw-semibold mb-2">
                Available exam formats
              </Form.Label>

              {/* Keep a real radio group for accessibility, but render as clickable cards */}
              <div
                className="d-grid gap-2"
                style={{
                  gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
                  alignItems: "start",
                }}
              >
                {EXAM_CONFIGURATION_OPTIONS.map((config) => {
                  const isSelected = selectedConfigId === config.id;
                  return (
                    <button
                      key={config.id}
                      type="button"
                      onClick={() => setSelectedConfigId(config.id)}
                      aria-pressed={isSelected}
                      className="text-start border rounded"
                      style={{
                        backgroundColor: isSelected ? "#0d6efd10" : "#fafafa",
                        borderColor: isSelected ? "#0d6efd" : undefined,
                        padding: 10,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className="d-flex"
                        style={{
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <div className="d-flex gap-3" style={{ minWidth: 0 }}>
                          {/* Keep a real radio input for a11y, but make the entire card clickable */}
                          <input
                            className="form-check-input"
                            type="radio"
                            name="examConfiguration"
                            id={`exam-config-${config.id}`}
                            checked={isSelected}
                            onChange={() => setSelectedConfigId(config.id)}
                            tabIndex={-1}
                            style={{
                              pointerEvents: "none",
                              marginTop: 2,
                              flex: "0 0 auto",
                            }}
                          />

                          <div style={{ minWidth: 0 }}>
                            <div
                              className="small fw-semibold"
                              style={{ lineHeight: 1.25 }}
                            >
                              {config.description}
                            </div>
                          </div>
                        </div>

                        <div
                          className="small text-muted"
                          style={{
                            textAlign: "right",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 14,
                            flex: "0 0 auto",
                            whiteSpace: "nowrap",
                            marginTop: 0,
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-end gap-1">
                            <BsClock size={12} />
                            <span>{config.durationMinutes} mins</span>
                          </div>
                          <div className="d-flex align-items-center justify-content-end gap-1">
                            <BsListOl size={12} />
                            <span>{config.totalQuestions} questions</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Form.Group>
          </Form>

          {!hasEnoughQuestions && selectedConfig && (
            <p className="small text-danger mt-3 mb-0">
              This subject does not have enough questions yet (
              {props.subjectTotalQuestions} available,{" "}
              {selectedConfig.totalQuestions} required).
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="dark"
            size="sm"
            form="timed-exam-form"
            type="submit"
            disabled={!selectedConfig || !hasEnoughQuestions}
          >
            Start exam
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TestExamModal;
